// Sprint 5 T12-v2 (Δ7) — modal que el auxiliar usa la primera vez que escanea
// el QR de un paciente, para capturar alergias verbalmente.
//
// Flujo en 2 pasos:
//   1) Pregunta: "El paciente conoce alergias?" → 3 botones
//      - Si registrar  → pasa al paso 2 (form)
//      - No conoce     → marca consultado con estado NIEGA, sin alergias
//      - No responde   → marca consultado con estado NO_RESPONDE, sin alergias
//   2) Form: lista de alergias { alimento, severidad, notas? } + boton "+ Otra"
//      Confirmacion extra cuando severidad = ANAFILACTICA.
//
// No usamos react-hook-form (regla portafolio: no instalar deps). useReducer
// para mantener estado del array de alergias capturadas.

import { useEffect, useReducer, useRef, useState } from 'react'
import {
  alergiasApi,
  ingredientesApi,
  type AlergiaCreate,
  type AlergiaResponse,
  type IngredienteSugerido,
  type SeveridadAlergia,
} from '../lib/api'
import type { EstadoConsulta } from '../stores/pacienteActualStore'
import ProtocoloStepper from './ProtocoloStepper'
import { ModalShell } from './ui/ModalShell'

type Paso = 'PREGUNTA' | 'FORM'

interface AlergiaDraft {
  // Estado de cada renglon antes de enviarlo al backend.
  alimento_id: number | null
  alimento_nombre: string
  grupo_alimentario: string
  severidad: SeveridadAlergia
  notas: string
}

type DraftAction =
  | { type: 'add' }
  | { type: 'remove'; index: number }
  | { type: 'update'; index: number; patch: Partial<AlergiaDraft> }

const SEVERIDADES: { value: SeveridadAlergia; label: string; descr: string }[] = [
  { value: 'LEVE',         label: 'Leve',          descr: 'Picor, enrojecimiento leve' },
  { value: 'MODERADA',     label: 'Moderada',      descr: 'Hinchazon, ronchas, malestar' },
  { value: 'SEVERA',       label: 'Severa',        descr: 'Vomito, dificultad respiratoria leve' },
  { value: 'ANAFILACTICA', label: 'Anafilactica',  descr: 'Riesgo de muerte — usar epinefrina' },
  { value: 'DESCONOCIDA',  label: 'Desconocida',   descr: 'Paciente no recuerda intensidad' },
]

function nuevoDraft(): AlergiaDraft {
  return {
    alimento_id: null,
    alimento_nombre: '',
    grupo_alimentario: '',
    severidad: 'MODERADA',
    notas: '',
  }
}

function draftReducer(state: AlergiaDraft[], action: DraftAction): AlergiaDraft[] {
  switch (action.type) {
    case 'add':
      return [...state, nuevoDraft()]
    case 'remove':
      return state.filter((_, i) => i !== action.index)
    case 'update':
      return state.map((d, i) => (i === action.index ? { ...d, ...action.patch } : d))
  }
}

interface AlergiaInicialModalProps {
  pacienteId: number
  pacienteNombre?: string
  habitacion?: string
  onCompletar: (estado: EstadoConsulta, alergiasCreadas: AlergiaResponse[]) => void
  onCancelar: () => void
}

export default function AlergiaInicialModal({
  pacienteId,
  pacienteNombre,
  habitacion,
  onCompletar,
  onCancelar,
}: AlergiaInicialModalProps) {
  const [paso, setPaso] = useState<Paso>('PREGUNTA')
  const [drafts, dispatch] = useReducer(draftReducer, [nuevoDraft()])
  const [enviando, setEnviando] = useState(false)
  const [errorGlobal, setErrorGlobal] = useState<string | null>(null)
  // Errores por renglon — se muestran solo despues de intentar enviar.
  const [erroresPorIdx, setErroresPorIdx] = useState<Record<number, string>>({})

  function handleNiega() {
    onCompletar('NIEGA', [])
  }
  function handleNoResponde() {
    onCompletar('NO_RESPONDE', [])
  }

  function validar(): boolean {
    const errs: Record<number, string> = {}
    drafts.forEach((d, i) => {
      if (!d.alimento_nombre.trim()) {
        errs[i] = 'Indica el alimento al que el paciente reacciona.'
      }
    })
    setErroresPorIdx(errs)
    return Object.keys(errs).length === 0
  }

  async function handleEnviar() {
    setErrorGlobal(null)
    if (!validar()) return

    // Confirmacion extra para ANAFILACTICA (riesgo vital).
    const tieneAnafilactica = drafts.some((d) => d.severidad === 'ANAFILACTICA')
    if (tieneAnafilactica) {
      const ok = window.confirm(
        'CONFIRMAR: estas registrando una alergia ANAFILACTICA.\n\n' +
          'Esto activara un banner rojo permanente en todas las pantallas y ' +
          'el paciente NO podra recibir platos con ese ingrediente.\n\n' +
          'Esta seguro?',
      )
      if (!ok) return
    }

    setEnviando(true)
    try {
      const creadas: AlergiaResponse[] = []
      for (const d of drafts) {
        // BUGFIX 5-jul (QA ALTO): el payload debe cumplir el schema del backend.
        // Si el auxiliar SELECCIONÓ un alimento del catálogo (alimento_id > 0)
        // se registra como 'alimento' (activa el bloqueo por grupo alérgeno);
        // si solo escribió texto libre, se guarda como 'grupo_alimentario'
        // (queda capturado y en el banner; el auto-bloqueo requiere el alimento
        // del catálogo).
        const tieneAlimento = d.alimento_id != null && d.alimento_id > 0
        const nombreLibre = d.grupo_alimentario.trim() || d.alimento_nombre.trim()
        const payload: AlergiaCreate = {
          paciente_id: pacienteId,
          tipo_alimento: tieneAlimento ? 'alimento' : 'grupo_alimentario',
          alimento_id: tieneAlimento ? (d.alimento_id as number) : 0,
          grupo_alimentario: tieneAlimento ? null : (nombreLibre || null),
          tipo_restriccion: 'ALERGIA',
          severidad: d.severidad,
          motivo_clinico: d.notas.trim() || null,
        }
        const created = await alergiasApi.crear(payload)
        creadas.push(created)
      }
      onCompletar('CONFIRMADO_CON_ALERGIAS', creadas)
    } catch (err: any) {
      // CRÍTICO Tanda 1 (5-jul): un fallo al guardar NUNCA debe marcar al
      // paciente como "NIEGA alergias" — eso convertía una alergia declarada
      // (incluso anafiláctica) en "sin alergias" y no se volvía a preguntar.
      // Ahora dejamos el modal ABIERTO con el error y el botón de reintento:
      // el auxiliar reintenta al volver la conexión, o cancela (cancelar NO
      // afirma nada clínico). Ninguna aserción falsa sale de un error de red.
      // Serializar el error: FastAPI devuelve `detail` como array de objetos en
      // errores 422 (antes se veía "[object Object],[object Object]").
      const d = err?.response?.data?.detail
      const detail = typeof d === 'string'
        ? d
        : Array.isArray(d)
          ? d.map((e: any) => e?.msg || String(e)).join('; ')
          : (err?.message || 'Error desconocido')
      setErrorGlobal(
        `No se pudo guardar en el servidor (${detail}). ` +
          'Las alergias NO se registraron — vuelve a intentar cuando haya conexión. ' +
          'Si cierras sin guardar, quedarán sin registrar (no se marca "niega").',
      )
    } finally {
      setEnviando(false)
    }
  }

  return (
    <ModalShell
      title="Alergias del paciente"
      subtitle={`${pacienteNombre ?? 'Paciente'}${habitacion ? ` · Hab. ${habitacion}` : ''}`}
      onClose={onCancelar}
      closeDisabled={enviando}
      maxWidth={560}
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancelar}
            className="text-xs text-gray-500 hover:underline"
          >
            Cerrar sin registrar
          </button>
        </div>
      }
    >
        <ProtocoloStepper pasoActual={2} />

        {paso === 'PREGUNTA' && (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-gray-800">
              <strong>Pregunta al paciente:</strong> &laquo;Tiene alguna alergia a comidas?&raquo;
            </p>
            <p className="text-xs text-gray-500">
              Esta consulta es obligatoria la primera vez del dia. Anota lo que el paciente declara.
            </p>

            <div className="grid gap-2">
              <button
                type="button"
                onClick={() => setPaso('FORM')}
                className="w-full bg-avante-primary text-white font-semibold py-3 rounded text-sm hover:opacity-90"
              >
                Si — registrar alergias
              </button>
              <button
                type="button"
                onClick={handleNiega}
                className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded text-sm hover:bg-gray-50"
              >
                No conoce alergias
              </button>
              <button
                type="button"
                onClick={handleNoResponde}
                className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded text-sm hover:bg-gray-50"
              >
                Paciente no responde / no puede comunicar
              </button>
            </div>
          </div>
        )}

        {paso === 'FORM' && (
          <div className="mt-4 space-y-4">
            {drafts.map((d, idx) => (
              <DraftRow
                key={idx}
                index={idx}
                draft={d}
                error={erroresPorIdx[idx]}
                onChange={(patch) => dispatch({ type: 'update', index: idx, patch })}
                onRemove={drafts.length > 1 ? () => dispatch({ type: 'remove', index: idx }) : undefined}
              />
            ))}

            <button
              type="button"
              onClick={() => dispatch({ type: 'add' })}
              className="text-sm text-avante-primary font-semibold hover:underline"
            >
              + Otra alergia
            </button>

            {errorGlobal && (
              <p
                role="alert"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3"
              >
                {errorGlobal}
              </p>
            )}

            <div className="flex gap-2 pt-2 border-t">
              <button
                type="button"
                onClick={() => setPaso('PREGUNTA')}
                className="flex-1 border border-gray-300 py-2 rounded text-sm"
                disabled={enviando}
              >
                ← Volver
              </button>
              <button
                type="button"
                onClick={handleEnviar}
                disabled={enviando}
                className="flex-1 bg-avante-success text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
              >
                {enviando ? 'Guardando...' : 'Guardar y continuar'}
              </button>
            </div>
          </div>
        )}
    </ModalShell>
  )
}

interface DraftRowProps {
  index: number
  draft: AlergiaDraft
  error?: string
  onChange: (patch: Partial<AlergiaDraft>) => void
  onRemove?: () => void
}

function DraftRow({ index, draft, error, onChange, onRemove }: DraftRowProps) {
  const fieldsetId = `alergia-${index}`
  return (
    <fieldset
      className="border border-gray-200 rounded-md p-3 space-y-3"
      aria-labelledby={`${fieldsetId}-legend`}
    >
      <div className="flex items-center justify-between">
        <legend id={`${fieldsetId}-legend`} className="text-sm font-semibold text-gray-800">
          Alergia #{index + 1}
        </legend>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-red-600 hover:underline"
            aria-label={`Eliminar alergia ${index + 1}`}
          >
            Eliminar
          </button>
        )}
      </div>

      <AlimentoCombobox
        valor={draft.alimento_nombre}
        onSeleccionar={(sug) =>
          onChange({
            alimento_id: sug?.id ?? null,
            alimento_nombre: sug?.nombre ?? draft.alimento_nombre,
            grupo_alimentario: sug?.grupo_alimentario ?? draft.grupo_alimentario,
          })
        }
        onTexto={(texto) => onChange({ alimento_id: null, alimento_nombre: texto })}
        error={error}
      />

      {/* Si el catalogo no esta disponible, el auxiliar puede tipear el grupo. */}
      <label className="block text-xs text-gray-700">
        Grupo alimentario (opcional)
        <input
          type="text"
          value={draft.grupo_alimentario}
          onChange={(e) => onChange({ grupo_alimentario: e.target.value })}
          placeholder="Ej: lacteos, frutos secos, mariscos"
          className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
        />
      </label>

      <fieldset className="border-0 p-0">
        <legend className="text-xs font-semibold text-gray-700 mb-1">
          Severidad de la reaccion
        </legend>
        <div className="grid gap-1">
          {SEVERIDADES.map((s) => {
            const id = `${fieldsetId}-sev-${s.value}`
            const danger = s.value === 'ANAFILACTICA'
            return (
              <label
                key={s.value}
                htmlFor={id}
                className={`flex items-start gap-2 p-2 rounded border text-sm cursor-pointer ${
                  draft.severidad === s.value
                    ? danger
                      ? 'border-red-600 bg-red-50'
                      : 'border-avante-primary bg-blue-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                <input
                  id={id}
                  type="radio"
                  name={`${fieldsetId}-severidad`}
                  value={s.value}
                  checked={draft.severidad === s.value}
                  onChange={() => onChange({ severidad: s.value })}
                  className="mt-1"
                />
                <span className="flex-1">
                  <span className={`font-semibold ${danger ? 'text-red-700' : 'text-gray-800'}`}>
                    {s.label}
                  </span>
                  <span className="block text-xs text-gray-500">{s.descr}</span>
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>

      <label className="block text-xs text-gray-700">
        Notas (opcional)
        <textarea
          value={draft.notas}
          onChange={(e) => onChange({ notas: e.target.value })}
          rows={2}
          className="mt-1 w-full border rounded px-2 py-1.5 text-sm"
          placeholder="Ej: solo crudo, no cocido; hace 5 anos sufrio shock..."
        />
      </label>
    </fieldset>
  )
}

interface AlimentoComboboxProps {
  valor: string
  onSeleccionar: (sug: IngredienteSugerido | null) => void
  onTexto: (texto: string) => void
  error?: string
}

// Combobox simple con debounce 250ms. Sin libs externas — useState + setTimeout.
function AlimentoCombobox({ valor, onSeleccionar, onTexto, error }: AlimentoComboboxProps) {
  const [sugerencias, setSugerencias] = useState<IngredienteSugerido[]>([])
  const [abierto, setAbierto] = useState(false)
  const debounceRef = useRef<number | null>(null)

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(async () => {
      const lista = await ingredientesApi.buscar(valor)
      setSugerencias(lista)
    }, 250)
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current)
    }
  }, [valor])

  return (
    <div className="relative">
      <label className="block text-xs text-gray-700">
        Alimento <span className="text-red-600">*</span>
        <input
          type="text"
          role="combobox"
          aria-expanded={abierto && sugerencias.length > 0}
          aria-controls="alimento-sugerencias"
          aria-autocomplete="list"
          aria-invalid={Boolean(error)}
          value={valor}
          onChange={(e) => {
            onTexto(e.target.value)
            setAbierto(true)
          }}
          onFocus={() => setAbierto(true)}
          onBlur={() => window.setTimeout(() => setAbierto(false), 150)}
          placeholder="Ej: mani, leche, camaron"
          className={`mt-1 w-full border rounded px-2 py-1.5 text-sm ${
            error ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
        />
      </label>
      {abierto && sugerencias.length > 0 && (
        <ul
          id="alimento-sugerencias"
          role="listbox"
          className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-md max-h-44 overflow-y-auto"
        >
          {sugerencias.map((s) => (
            <li
              key={s.id}
              role="option"
              aria-selected={false}
              className="px-2 py-1.5 text-sm hover:bg-blue-50 cursor-pointer flex items-center justify-between gap-2"
              onMouseDown={(e) => {
                e.preventDefault()
                onSeleccionar(s)
                setAbierto(false)
              }}
            >
              <span>
                {s.nombre}
                {s.grupo_alimentario && (
                  <span className="text-xs text-gray-500 ml-1">({s.grupo_alimentario})</span>
                )}
              </span>
              {s.es_alergeno && (
                <span className="text-[10px] font-bold text-red-700 bg-red-100 rounded px-1.5 py-0.5 shrink-0">
                  ⚠ alérgeno{s.tipo_alergeno ? ` · ${s.tipo_alergeno}` : ''}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-xs text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  )
}
